import {
  closestCorners,
  CollisionDetection,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  DropAnimation,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useRef, useState } from "react";

import { FieldComponentType, Toast } from "@smart/bridge-intake-components-dom";
import { roleProviderId } from "@smart/bridge-smokeball-basic";
import { FieldNS, GroupNS } from "@smart/bridge-types-basic";
import {
  rankBetween,
  removeEmptyValues,
  removeKeys,
} from "@smart/itops-utils-basic";
import { Gql } from "@smart/manage-gql-operations-dom";

import {
  checkIsFieldComponent,
  DropResult,
  flattenItems,
  getActionType,
  getOrder,
  getPositions,
  isMappedField,
  updateDisplayingSectionItemOnDragEnd,
  updateDisplayingSectionItemOnMovingInOrOutOfGroup,
} from "./drag-and-drop-helpers";
import { useLastActiveIds } from "./last-active-ids";
import {
  fieldComponentContainerDroppableId,
  fieldComponentNames,
} from "../constants";
import { useUpdateData } from "../hooks";
import { ContactDetailsOptions } from "../questions/contact-details-modal";
import { MappedFieldsOptions } from "../questions/mapped-fields-modal";
import { buildFixedSelectionForGroup } from "../questions/mapped-fields-modal/helpers";
import {
  checkIsSection,
  DropAction,
  GqlField,
  GqlFieldValues,
  GqlGroup,
  GqlGroupValues,
  GqlGroupWithFieldValues,
  GqlSectionItem,
  isSectionItemGroup,
  QuestionEditMode,
} from "../types";
import {
  extractDroppableFieldPosition,
  isContactDetailsGroup,
  isFieldSeparatorId,
  isGroupSeparatorId,
} from "../utils";
import { getOrderedSectionItems } from "../utils/section-items";

export const useSectionItemOrder = ({
  sectionFields,
  sectionItems,
  addField,
  addGroup,
  changeFieldOrder,
  changeGroupOrder,
  updateFieldGroup,
  fields,
  groups,
  mergeUpdateField,
  updateGroup,
  setToasts,
}: {
  sectionFields: GqlField[] | undefined;
  sectionItems: GqlSectionItem[];
  addField: ReturnType<typeof useUpdateData>["addField"];
  addGroup: ReturnType<typeof useUpdateData>["addGroup"];
  changeFieldOrder: ReturnType<typeof useUpdateData>["changeFieldOrder"];
  changeGroupOrder: ReturnType<typeof useUpdateData>["changeGroupOrder"];
  updateFieldGroup: ReturnType<typeof useUpdateData>["updateFieldGroup"];
  mergeUpdateField: ReturnType<typeof useUpdateData>["mergeUpdateField"];
  updateGroup: ReturnType<typeof useUpdateData>["updateGroup"];
  fields?: GqlField[] | null;
  groups?: GqlGroup[] | null;
  setToasts?: (toasts: Toast[]) => void;
}) => {
  const [sectionItemsToDisplay, setSectionItemsToDisplay] =
    useState<GqlSectionItem[]>(sectionItems);
  const [mappedFieldsOptions, setMappedFieldsOptions] =
    useState<MappedFieldsOptions>();
  const [contactDetailsOptions, setContactDetailsOptions] =
    useState<ContactDetailsOptions>();
  const [questionEditMode, setQuestionEditMode] =
    useState<QuestionEditMode>("none");
  const [pendingDropAction, setPendingDropAction] = useState<DropAction>();
  const [activeId, setActiveId] = useState<string>();
  const [overId, setOverId] = useState<string>();
  const [delta, setDelta] = useState<
    | {
        x: number;
        y: number;
      }
    | undefined
  >();
  const refDropResult = useRef<DropResult | undefined>(undefined);
  const { lastActiveIds, setLastActiveIds } = useLastActiveIds(sectionItems);

  useEffect(() => {
    setSectionItemsToDisplay(sectionItems);
  }, [sectionItems]);

  const checkDropIsValid = (
    field: { type: string | null },
    destinationGroup: { repeatable?: boolean | null; type?: string | null },
    sourceGroup?: { type?: string | null },
  ): boolean => {
    if (field.type === "payment" && destinationGroup.repeatable) {
      setToasts?.([
        {
          text: "Smokeball Payments can't be used in repeatable groups. Place this component outside the group.",
          palette: "warning",
          width: "medium",
        },
      ]);
      // Revert visual state to original since the drop is invalid
      setSectionItemsToDisplay(sectionItems);
      return false;
    }
    if (
      isContactDetailsGroup(destinationGroup) ||
      isContactDetailsGroup(sourceGroup)
    ) {
      setToasts?.([
        {
          text: "Action not allowed for Contact Details component.",
          palette: "warning",
          width: "medium",
        },
      ]);
      setSectionItemsToDisplay(sectionItems);
      return false;
    }
    return true;
  };

  const handleFieldDroppedOnGroup = async ({
    groupUri,
    type,
    beforePosition,
    afterPosition,
  }: {
    groupUri: string;
    type: FieldComponentType;
    beforePosition?: string;
    afterPosition?: string;
  }) => {
    const group = sectionItems.find(
      (item) => item.uri === groupUri,
    ) as GqlGroupWithFieldValues;

    if (!checkDropIsValid({ type }, group)) return;

    const groupMappedFields = sectionFields?.filter(
      (sf) =>
        sf.values.groupUri === groupUri && sf.values.layout && sf.values.field,
    );
    /**
     * If the group has a role provider layout, always use that for fixedSelection
     * to prevent it from changing to a sub layout when sub layout fields are added
     */
    const roleField = groupMappedFields?.find(
      (f) => f.values.layout?.providerId === roleProviderId,
    );
    const groupMappedField = roleField || groupMappedFields?.[0];
    const layout =
      group?.layout || groupMappedField?.values.layout || undefined;

    switch (type) {
      case "group":
        console.error("cannot place a group within a group");
        break;
      case "mapped":
        setMappedFieldsOptions({
          fixedSelection: buildFixedSelectionForGroup({
            layout,
            groupField: group?.field,
            fallbackField: groupMappedField?.values.field,
          }),
          beforePosition,
          afterPosition,
          destinationGroupUri: groupUri,
        });
        break;
      case "contactDetails":
        // TODO: can contact details templates be used in groups?
        break;
      // eslint-disable-next-line no-fallthrough
      default:
        await addField({
          type,
          groupUri,
          order: rankBetween({
            after: afterPosition,
            before: beforePosition,
          }),
        });
    }
  };

  const handleFieldDroppedOnForm = async ({
    type,
    beforePosition,
    afterPosition,
  }: {
    type: FieldComponentType;
    beforePosition?: string;
    afterPosition?: string;
  }) => {
    const order = rankBetween({
      after: afterPosition,
      before: beforePosition,
    });

    switch (type) {
      case "group":
        await addGroup(order);
        break;
      case "mapped":
        setMappedFieldsOptions({
          beforePosition,
          afterPosition,
        });
        break;
      case "contactDetails":
        setContactDetailsOptions({
          beforePosition,
          afterPosition,
          entryPoint: "Contact Details Component",
        });
        break;
      default:
        await addField({ type, order });
    }
  };

  const handleAddItem = async ({
    fieldComponentId,
    overId: overIdFromDropResult,
  }: {
    fieldComponentId: string;
    overId: string | undefined;
  }) => {
    if (!overIdFromDropResult) return;

    const toPosition = extractDroppableFieldPosition(overIdFromDropResult);
    if (toPosition === -1) return;

    const fieldComponentType = fieldComponentId
      .split("-")
      .pop() as FieldComponentType;
    if (!Object.keys(fieldComponentNames).includes(fieldComponentType)) return;

    if (isGroupSeparatorId(overIdFromDropResult)) {
      const groupUri = overIdFromDropResult.split("|")[1];
      const group = sectionItems.find(
        (i) => i.uri === groupUri,
      ) as GqlGroupWithFieldValues;

      const { afterPosition, beforePosition } = getPositions(
        group.fields || [],
        toPosition,
      );
      await handleFieldDroppedOnGroup({
        groupUri,
        type: fieldComponentType,
        beforePosition,
        afterPosition,
      });
    } else if (isFieldSeparatorId(overIdFromDropResult)) {
      const { afterPosition, beforePosition } = getPositions(
        sectionItems,
        toPosition,
      );
      await handleFieldDroppedOnForm({
        type: fieldComponentType,
        beforePosition,
        afterPosition,
      });
    }
  };

  const handleChangeFieldSection = async (
    fieldToBeMoved: GqlFieldValues,
    newSectionUri: string,
    newOrder: {
      afterPosition: string | undefined;
      beforePosition: string | undefined;
    },
  ) =>
    mergeUpdateField({
      current: fieldToBeMoved,
      incoming: {
        sectionUri: newSectionUri,
        order: rankBetween({
          after: newOrder.afterPosition,
          before: newOrder.beforePosition,
        }),
      },
    });

  const handleChangeGroupSection = async (
    groupToBeMoved: GqlGroupValues,
    newSectionUri: string,
    newOrder: {
      afterPosition: string | undefined;
      beforePosition: string | undefined;
    },
  ) => {
    const {
      uri,
      formUri,
      hint,
      label,
      type,
      layout,
      field,
      allowedRepeatable,
      description,
      maxRepeat,
      minRepeat,
      repeatable,
      templateType,
    } = groupToBeMoved;

    await updateGroup({
      variables: {
        uri,
        order: rankBetween({
          after: newOrder.afterPosition,
          before: newOrder.beforePosition,
        }),
        formUri,
        sectionUri: newSectionUri,
        fields: {
          hint,
          type,
          label,
          layout: layout
            ? ({
                ...removeEmptyValues(
                  removeKeys(layout, ["__typename", "displayName"]),
                ),
                parentId: layout.parentId || undefined,
                parentName: layout.parentName || undefined,
                parentProviderId: layout.parentProviderId || undefined,
              } as Gql.UpdateFieldMutationVariables["fields"]["layout"])
            : undefined,
          field: field
            ? {
                ...removeKeys(field, ["__typename", "displayName", "details"]),
                possibleValues: field.possibleValues || undefined,
                dataSourceMapping: field.dataSourceMapping || undefined,
                originalName: field.originalName || undefined,
              }
            : undefined,
          links: undefined,
          allowedRepeatable: allowedRepeatable || undefined,
          description: description || undefined,
          maxRepeat: maxRepeat || undefined,
          minRepeat: minRepeat || undefined,
          repeatable: repeatable || undefined,
          templateType: templateType || undefined,
        },
      },
    }).catch(console.error);
  };

  const onItemDroppedInSection = async (dropResult: DropResult) => {
    if (!sectionFields || !dropResult.overId || !sectionItems) return;

    const { overId: sectionUri, activeId: draggableId } = dropResult;
    const newOrder = getPositions(
      getOrderedSectionItems({
        sectionUri,
        fields: fields || [],
        groups: groups || [],
      }),
      0,
    );

    if (GroupNS.isUriMatch(draggableId)) {
      const group = groups?.find((g) => g.uri === draggableId);
      if (group)
        await handleChangeGroupSection(group.values, sectionUri, newOrder);
    } else if (FieldNS.isUriMatch(draggableId)) {
      const field = sectionFields.find((f) => f.uri === draggableId);
      if (field && !field.values.groupUri)
        await handleChangeFieldSection(field.values, sectionUri, newOrder);
    }
  };

  const handleChangingSectionItemOrder = async (dropResult: DropResult) => {
    const {
      activeId: activeItemId,
      activeItem,
      overId: overIdFromDropResult,
    } = dropResult;
    if (activeItemId === overIdFromDropResult) return;
    const overIndex = sectionItems.findIndex(
      (i) => i.uri === overIdFromDropResult,
    );
    const isMovingDown = dropResult.delta.y > 0;
    const destinationIndex =
      overIndex >= 0 ? overIndex + (isMovingDown ? 1 : 0) : sectionItems.length;
    const order = getOrder(destinationIndex, sectionItems);

    await (isSectionItemGroup(activeItem)
      ? changeGroupOrder(activeItemId, order)
      : changeFieldOrder(activeItemId, order));
  };

  const handleChangingGroupFieldOrder = async (dropResult: DropResult) => {
    if (dropResult.activeId === dropResult.overId) return;
    const field = dropResult.activeDisplayItem as GqlFieldValues;
    const groupFields = (
      sectionItems?.find(
        (g) => g.uri === field.groupUri,
      ) as GqlGroupWithFieldValues
    ).fields;

    const overIndex = groupFields.findIndex((f) => f.uri === dropResult.overId);
    const isMovingDown = dropResult.delta.y > 0;
    const destinationIndex =
      overIndex >= 0 ? overIndex + (isMovingDown ? 1 : 0) : groupFields.length;
    await changeFieldOrder(field?.uri, getOrder(destinationIndex, groupFields));
  };

  const handleChangingFieldGroup = async (
    dropResult: DropResult,
    shouldRemoveMappedLayout?: boolean,
  ) => {
    const field = dropResult.activeDisplayItem as GqlFieldValues;
    const groupUri = field.groupUri as string;
    const group = sectionItems?.find(
      (g) => g.uri === groupUri,
    ) as GqlGroupWithFieldValues;

    if (!checkDropIsValid(field, group)) {
      return;
    }

    if (
      !shouldRemoveMappedLayout &&
      isMappedField(field) &&
      (group.type !== "custom" || group.repeatable)
    ) {
      setPendingDropAction("MOVE_FIELD_INTO_GROUP");
      refDropResult.current = dropResult;
    } else {
      /**
       * When a field is moved into a group, it's placed at the end of the group
       * so if it's over itself, it'll be at the end of the group
       */
      const overIndex = group.fields.findIndex(
        (f) => f.uri === dropResult.overId,
      );
      const overIndexToUse = overIndex >= 0 ? overIndex : group.fields.length;
      const destinationIndex =
        dropResult.activeId === dropResult.overId
          ? group.fields.length
          : overIndexToUse;
      await updateFieldGroup(
        field.uri,
        groupUri,
        getOrder(destinationIndex, group.fields),
        shouldRemoveMappedLayout,
      );
    }
  };

  const handleMovingGroupFieldToSection = async (
    dropResult: DropResult,
    shouldRemoveMappedLayout?: boolean,
  ) => {
    const field = dropResult.activeItem as GqlFieldValues;

    const group = sectionItems?.find(
      (g) => g.uri === field.groupUri,
    ) as GqlGroupWithFieldValues;

    if (!checkDropIsValid(field, group)) {
      return;
    }

    if (
      !shouldRemoveMappedLayout &&
      isMappedField(field) &&
      (group.type !== "custom" || group.repeatable)
    ) {
      setPendingDropAction("MOVE_FIELD_OUT_OF_GROUP");
      refDropResult.current = dropResult;
    } else {
      const fieldPositionInDisplay = sectionItemsToDisplay.findIndex(
        (i) => i.uri === field.uri,
      );

      let destinationIndex = 0;
      if (fieldPositionInDisplay >= 0) {
        destinationIndex = fieldPositionInDisplay;
      } else {
        const overIndex = sectionItemsToDisplay.findIndex(
          (i) => i.uri === dropResult.overId,
        );
        const firstOrLastItemIndex =
          dropResult.delta.y > 0 ? sectionItemsToDisplay.length : 0;
        destinationIndex = overIndex >= 0 ? overIndex : firstOrLastItemIndex;
      }

      await updateFieldGroup(
        field.uri,
        undefined,
        getOrder(destinationIndex, sectionItems),
        shouldRemoveMappedLayout,
      );
    }
  };

  const handleMovingFieldBetweenGroups = async (
    dropResult: DropResult,
    shouldRemoveMappedLayout?: boolean,
  ) => {
    const field = dropResult.activeItem as GqlFieldValues;
    const sourceGroupUri = field.groupUri as string;

    const displayField = dropResult.activeDisplayItem as GqlFieldValues;
    const destinationGroupUri = displayField.groupUri as string;

    if (!destinationGroupUri || sourceGroupUri === destinationGroupUri) {
      return;
    }

    const sourceGroup = sectionItems?.find((g) => g.uri === sourceGroupUri) as
      | GqlGroupWithFieldValues
      | undefined;

    const destinationGroup = sectionItems?.find(
      (g) => g.uri === destinationGroupUri,
    ) as GqlGroupWithFieldValues | undefined;

    if (!sourceGroup || !destinationGroup) return;

    if (!checkDropIsValid(field, destinationGroup, sourceGroup)) {
      return;
    }

    if (
      !shouldRemoveMappedLayout &&
      isMappedField(field) &&
      (sourceGroup.type !== "custom" || sourceGroup.repeatable)
    ) {
      setPendingDropAction("MOVE_FIELD_BETWEEN_GROUPS");
      refDropResult.current = dropResult;
    } else {
      let destinationIndex = destinationGroup.fields.length;

      if (dropResult.overItem && !isSectionItemGroup(dropResult.overItem)) {
        const overIndex = destinationGroup.fields.findIndex(
          (f) => f.uri === dropResult.overId,
        );
        if (overIndex >= 0) {
          destinationIndex = overIndex;
        }
      }

      await updateFieldGroup(
        field.uri,
        destinationGroupUri,
        getOrder(destinationIndex, destinationGroup.fields),
        shouldRemoveMappedLayout,
      );
    }
  };

  const cancelDropAction = () => setPendingDropAction(undefined);

  const confirmDropAction = async () => {
    setPendingDropAction(undefined);
    if (!refDropResult.current) return;

    switch (pendingDropAction) {
      case "MOVE_FIELD_INTO_GROUP":
        await handleChangingFieldGroup(refDropResult.current, true);
        break;
      case "MOVE_FIELD_OUT_OF_GROUP":
        await handleMovingGroupFieldToSection(refDropResult.current, true);
        break;
      case "MOVE_FIELD_BETWEEN_GROUPS":
        await handleMovingFieldBetweenGroups(refDropResult.current, true);
        break;
      default:
        break;
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
  );

  const collisionDetectionAlgorithm = (
    args: Parameters<CollisionDetection>[0],
  ) => {
    if (checkIsFieldComponent(args.active.id as string))
      return closestCorners(args);

    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }

    const rectCollisions = rectIntersection(args);

    return rectCollisions;
  };

  const dropAnimation: DropAnimation = {
    keyframes({ transform, active }) {
      const disappearAnimation = [{ opacity: 1 }, { opacity: 0 }];
      const getMoveToDestinationAnimation = ({
        verticalOnly,
      }: { verticalOnly?: boolean } = {}) => [
        { transform: CSS.Transform.toString(transform.initial) },
        {
          transform: CSS.Transform.toString(
            verticalOnly
              ? {
                  ...transform.final,
                  x: 0,
                }
              : transform.final,
          ),
        },
      ];
      if (checkIsSection(overId)) return disappearAnimation;
      if (checkIsFieldComponent(active.id as string))
        return overId === fieldComponentContainerDroppableId
          ? getMoveToDestinationAnimation()
          : disappearAnimation;

      return getMoveToDestinationAnimation({ verticalOnly: true });
    },
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setOverId(undefined);
    const activeItemId = active.id.toString();
    setActiveId(activeItemId);

    if (checkIsFieldComponent(activeItemId)) {
      setQuestionEditMode("creating");
    } else if (
      activeItemId.match(FieldNS.uriRegex) ||
      activeItemId.match(GroupNS.uriRegex)
    ) {
      setQuestionEditMode("reordering");
    }
  };

  const onDragMove = ({ delta: deltaToUpdate, active }: DragMoveEvent) => {
    if (checkIsFieldComponent(active.id.toString())) setDelta(deltaToUpdate);
  };

  /**
   * When dragging, we only change the display data when moving a field into a group
   * or out of a group. Otherwise, the position of the items are handled by the transform
   * css rules provided by the library (the actual display data is not changed).
   */
  const onDragOver = ({
    active,
    over,
    delta: deltaFromDragOver,
  }: DragOverEvent) => {
    setOverId(over?.id as string);
    const originalField = sectionFields?.find((f) => f.uri === active.id);
    if (checkIsFieldComponent(active.id.toString()) || !originalField) return;

    // Get the Y position of the dragged element to determine if it's in the group's field container
    const activeRect = active.rect.current.translated;
    const dropPointerY = activeRect
      ? activeRect.top + activeRect.height / 2
      : undefined;

    updateDisplayingSectionItemOnMovingInOrOutOfGroup({
      setSectionItemsToDisplay,
      activeId: active.id.toString(),
      overId: over?.id.toString(),
      delta: deltaFromDragOver,
      originalField,
      dropPointerY,
    });
  };

  const onDragEnd = async ({
    active,
    over,
    delta: deltaToUpdate,
  }: DragEndEvent) => {
    setDelta(undefined);
    setActiveId(undefined);
    setQuestionEditMode("none");
    const activeItemId = active.id.toString();
    const overIdOnDragEnd = over?.id.toString();
    const flattenedItems = flattenItems(sectionItems);
    const activeItem = flattenedItems.find((item) => item.uri === activeItemId);
    const overItem = flattenedItems.find(
      (item) => item.uri === overIdOnDragEnd,
    );
    const flattenedDisplayItems = flattenItems(sectionItemsToDisplay);
    const activeDisplayItem = flattenedDisplayItems.find(
      (item) => item.uri === active.id,
    );
    if (checkIsFieldComponent(active.id.toString())) {
      await handleAddItem({
        fieldComponentId: activeItemId,
        overId: overIdOnDragEnd,
      });
      return;
    }

    // This is to make the update instant so the items don't reorder after the data is updated
    updateDisplayingSectionItemOnDragEnd({
      setSectionItemsToDisplay,
      activeId: activeItemId,
      overId: overIdOnDragEnd,
      flattenedDisplayItems,
      activeDisplayItem,
    });

    if (!activeItem || !activeDisplayItem) return;

    setLastActiveIds([active.id as string]);

    const dragResult = {
      activeId: activeItemId,
      overId: overIdOnDragEnd,
      activeItem,
      overItem,
      activeDisplayItem,
      delta: deltaToUpdate,
    };
    switch (getActionType(dragResult)) {
      case "MOVE_ITEM_TO_SECTION":
        await onItemDroppedInSection(dragResult);
        break;
      case "MOVE_FIELD_INTO_GROUP":
        await handleChangingFieldGroup(dragResult);
        break;
      case "MOVE_FIELD_OUT_OF_GROUP":
        await handleMovingGroupFieldToSection(dragResult);
        break;
      case "MOVE_FIELD_BETWEEN_GROUPS":
        await handleMovingFieldBetweenGroups(dragResult);
        break;
      case "CHANGE_GROUP_FIELD_ORDER":
        await handleChangingGroupFieldOrder(dragResult);
        break;
      case "CHANGE_SECTION_ITEM_ORDER":
        await handleChangingSectionItemOrder(dragResult);
        break;
      default:
        break;
    }
  };

  return {
    activeId,
    lastActiveIds,
    delta,
    onDragStart,
    onDragMove,
    onDragOver,
    onDragEnd,
    mappedFieldsOptions,
    setMappedFieldsOptions,
    contactDetailsOptions,
    setContactDetailsOptions,
    questionEditMode,
    pendingDropAction,
    cancelDropAction,
    confirmDropAction,
    sensors,
    collisionDetectionAlgorithm,
    dropAnimation,
    sectionItemsToDisplay,
  };
};
